import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@upme/ui/components/button';
import { useState } from 'react';
import { ProviderForm } from '../../../features/payment-providers/components/provider-form';
import { useUpdateProvider } from '../../../features/payment-providers/mutations';
import { listProvidersOptions } from '../../../features/payment-providers/queries';

export const Route = createFileRoute('/_authenticated/payment-providers/$providerId')({
  staticData: { breadcrumb: 'Провайдер' },
  loader: ({ context }) => context.queryClient.ensureQueryData(listProvidersOptions()),
  component: EditProviderPage,
});

/**
 * Публичный URL вебхука для PayPal-кабинета. База — та же, на которой admin-ui
 * общается с API (VITE_API_BASE_URL, в проде = API_PUBLIC_URL = https://api.upme.click).
 * Путь без префикса /api: у API нет глобального префикса (health тоже на root).
 */
function buildWebhookUrl(providerId: string): string {
  const base = (import.meta.env.VITE_API_BASE_URL ?? '<API_PUBLIC_URL>').replace(/\/$/, '');
  return `${base}/payments/webhooks/${providerId}`;
}

function WebhookUrlCard({ providerId }: { providerId: string }) {
  const webhookUrl = buildWebhookUrl(providerId);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md bg-muted p-3 text-sm">
      <div className="font-medium">Webhook URL для PayPal-кабинета</div>
      <code className="block break-all rounded bg-background px-2 py-1.5">{webhookUrl}</code>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
          {copied ? 'Скопировано ✓' : 'Копировать'}
        </Button>
        <span className="text-xs text-muted-foreground">событие: PAYMENT.CAPTURE.COMPLETED</span>
      </div>
    </div>
  );
}

function EditProviderPage() {
  const { providerId } = Route.useParams();
  const navigate = useNavigate();
  const { data: providers } = useSuspenseQuery(listProvidersOptions());
  const existing = providers.find((p) => p.id === providerId);
  const update = useUpdateProvider(providerId);

  if (!existing) return <div className="p-4">Провайдер не найден</div>;

  return (
    <div className="max-w-lg space-y-4 p-4">
      <h1 className="text-xl font-bold">{existing.name}</h1>
      <WebhookUrlCard providerId={providerId} />
      <ProviderForm
        existing={existing}
        pending={update.isPending}
        onSubmit={(values) =>
          update.mutate(values, { onSuccess: () => navigate({ to: '/payment-providers' }) })
        }
      />
    </div>
  );
}
